import _ from 'lodash';

import * as Service from 'types/service';
import { Button } from '@tock/shared/components/buttons/DeprecatedButton';
import { IWithContextProps, withContext } from '@tock/coretypes';
import { IconChevronSmallRight } from '@tock/icons';
import { PrintChitSource } from 'constants/PrinterConstants';
import { StateSelectorStatus } from 'components/service/party-panel/PartyPanelFooter';
import { WithAsyncRenderProps, withAsyncRender } from 'contexts/asyncRender';
import {
  canMarkPartyAsLeft,
  findPrimaryStateInList,
  getDisplayInfoForPartyState,
  getNextStatusIndex,
  isPartyOriginallyWalkIn,
} from 'utils/partyUtils';
import BaseComponent from '@tock/shared/components/BaseComponent';
import BasicDropdown from '@tock/shared/components/inputs/basic-dropdown/BasicDropdown';
import Field from '@tock/shared/components/forms/Field';
import PartyStates from 'constants/partyStateConstants';
import SecondaryStateItem from 'components/service/dropdown/SecondaryStateItem';

export interface StateSelectorProps {
  handleUpdatePartyState(party: Service.Party, state: string, secondaryState: string | null): void;
  isDisabled: boolean;
  party: Service.Party;
  position?: 'bottom' | 'top';
  printOnSeat: boolean;
  status: StateSelectorStatus;
  secondaryStates?: { [key: string]: Service.StateDescription[] };
  modifierClassName?: string;
  onPrintChit?(party: Service.Party, source: PrintChitSource): void;
}

type Props = StateSelectorProps & IWithContextProps & WithAsyncRenderProps;

interface State {
  options: Service.StateDescription[];
}

class PartyPanelStateSelector extends BaseComponent<Props, State> {
  state = {
    options: [],
  };

  static defaultProps = {
    position: 'top' as StateSelectorProps['position'],
  };

  constructor(props: Props) {
    super(props);
  }

  handleUpdatePartyState(option: Service.StateDescription): void {
    const {
      asyncRender,
      context: { isFeatureEnabled },
      handleUpdatePartyState,
      party,
      printOnSeat,
      onPrintChit,
    } = this.props;
    if (
      printOnSeat &&
      onPrintChit &&
      ((option.primaryState === 'PARTIALLY_SEATED' && isFeatureEnabled('PRINT_PARTIALLY_SEATED')) ||
        option.primaryState === 'SEATED') &&
      option.secondaryValue === null
    ) {
      onPrintChit(party, PrintChitSource.AUTOMATIC);
    }

    if (!canMarkPartyAsLeft(party) && option.primaryState === 'LEFT') {
      asyncRender
        .confirm(
          `You haven’t collected payment from this party yet. Are you sure you’d like to mark them as finished?`
        )
        .then(() => handleUpdatePartyState(party, option.primaryState, option.secondaryValue))
        .catch(_.noop);
      return;
    }
    handleUpdatePartyState(party, option.primaryState, option.secondaryValue);
  }

  static getProperStateStyling(
    stateDescriptions: Service.StateDescription[],
    primaryState: Service.PartyState
  ): Service.StateDescription {
    return (
      findPrimaryStateInList(stateDescriptions, primaryState) ||
      getDisplayInfoForPartyState(primaryState)
    );
  }

  // this method extracts the ordering and adds an individual state to certain lists of states depending on the party panel
  static getDerivedStateFromProps(props: Props): State {
    const { party, status, secondaryStates } = props;
    if (!secondaryStates) {
      return {
        options: [],
      };
    }
    const seatedState = PartyPanelStateSelector.getProperStateStyling(
      secondaryStates.SEATED,
      PartyStates.seated
    );
    const expectedState = PartyPanelStateSelector.getProperStateStyling(
      secondaryStates.EXPECTED,
      PartyStates.expected
    );
    const noShowState = !isPartyOriginallyWalkIn(party) ? secondaryStates.NO_SHOW : []; // You can't no-show a walkin
    const partiallySeatedState = findPrimaryStateInList(
      secondaryStates.EXPECTED,
      PartyStates.partiallyseated
    );
    switch (status) {
      case 'EXPECTED_TABLE':
        return {
          options: _.flattenDeep(
            _.concat(_.sortBy(secondaryStates.EXPECTED, 'ordering'), seatedState, noShowState)
          ),
        };
      case 'EXPECTED_NO_TABLE':
        return {
          options: _.flattenDeep(
            _.concat(
              _.filter(
                _.sortBy(secondaryStates.EXPECTED, 'ordering'),
                (stateOption) => stateOption.primaryState !== PartyStates.partiallyseated
              ),
              noShowState
            )
          ),
        };
      case 'SEATED': {
        const allButNoShowAndLeft = _.concat(
          expectedState,
          partiallySeatedState || [],
          _.sortBy(secondaryStates.SEATED, 'ordering')
        );
        return {
          options: _.flattenDeep(_.concat(allButNoShowAndLeft, secondaryStates.LEFT, noShowState)),
        };
      }
      case 'FINISHED_TABLE':
        return {
          options: _.flattenDeep(
            _.concat([expectedState], [seatedState], secondaryStates.LEFT, noShowState)
          ),
        };
      case 'FINISHED_NO_TABLE': {
        const expectedStates = _.concat(secondaryStates.EXPECTED, [expectedState]);
        return {
          options: _.flattenDeep(
            _.concat(_.sortBy(expectedStates, 'ordering'), secondaryStates.LEFT, noShowState)
          ),
        };
      }
      case 'NO_SHOW':
        return {
          options: _.flattenDeep(_.concat([expectedState], noShowState)),
        };
      case 'CANCELLED':
        return {
          options: secondaryStates.CANCELED,
        };
      default:
        return { options: [] };
    }
  }

  handleGoToNextStatus() {
    const { party } = this.props;
    const { options } = this.state;

    const nextOptionIndex = getNextStatusIndex(party, options);

    this.handleUpdatePartyState(options[nextOptionIndex]);
  }

  render() {
    const { isDisabled, party, modifierClassName } = this.props;
    const selectedOption = party.stateDescription;

    return (
      <Field className={modifierClassName} key="state">
        <div className={'PartyStateSelector-state'} data-testid="party-status">
          <BasicDropdown
            contextClassName={'PartyStateSelector-dropdown'}
            component={SecondaryStateItem}
            position={this.props.position}
            disabled={isDisabled}
            hideChevron={true}
            options={this.state.options}
            selectedOption={selectedOption}
            onSelect={this.handleUpdatePartyState}
          />
          <Button
            modifierClassName="PartyStateSelector-next"
            size="small"
            onClick={this.handleGoToNextStatus}
          >
            <IconChevronSmallRight className="PartyStateSelector-next-icon" />
          </Button>
        </div>
      </Field>
    );
  }
}

export default withContext<StateSelectorProps>(withAsyncRender(PartyPanelStateSelector));
